/* eslint-disable no-console */
import { useCallback, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import jwt_decode from "jwt-decode";
import { ThemeProvider } from "styled-components";

import { getUserSiteMap } from "@/api/user";
import {
    EventType,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from "@/auth-stub/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@/auth-stub/msal-react";
import { msalConfig } from "@/authConfig";
import { ApplicationProvider, useApplication } from "@/context/application";
import { FeatureTogglesProvider } from "@/context/feature-toggles";
import { ModalProvider } from "@/context/modal/modal";
import { RouteHistoryProvider } from "@/context/route-history/route-history";
import { UserProvider, useUser } from "@/context/user";
import { SET_ROLE, SET_TOKEN } from "@/context/user/reducer";
import Background from "@/modules/background";
import Navigation from "@/modules/navigation";
import TopBar from "@/modules/topbar";

import PrivacyStatement from "@/pages/onboarding/privacy-statement";
import SurveySuccess from "@/pages/onboarding/success";
import OnboardingSurvey from "@/pages/onboarding/survey";
import OverviewPage from "@/pages/overview";
import PromoPage from "@/pages/promo";
import Reports from "@/pages/reports";
import SessionDetail from "@/pages/session-detail";
import SessionsPage from "@/pages/sessions";
import Settings from "@/pages/settings";
import UsersPage from "@/pages/users/users";

import VRIntlSuspender, { VRIntlProviderComponent } from "@/components/providers/intl-provider";
import GenericError from "@/components/ui/generic-error";
import Loader from "@/components/ui/loader";
import ModalWithContext from "@/components/ui/modal-with-context/modal-with-context";

import base from "@/themes/base/base";
import GlobalStyle from "@/themes/global";

import { HeadlessPages, Pages } from "@/enums/pages";
import { HeatRoles, Roles } from "@/enums/user";

import { isDev } from "./dev";

import { LoaderContainer, Wrapper } from "./app.styles";

import type { StubPublicClientApplication } from "@/auth-stub/msal-browser";
import type { EventMessage } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-common";

declare global {
    interface Window {
        hasVerifiedUser?: boolean;
    }
}

const isJwtLike = (token: unknown): token is string =>
    typeof token === "string" && token.split(".").length === 3;

const safeJwtDecode = (token: unknown): any | null => {
    if (!isJwtLike(token)) return null;

    try {
        return jwt_decode(token);
    } catch {
        return null;
    }
};

const loginRequest = {
    scopes: [process.env.REACT_APP_MSAL_LOGIN_REQUEST_SCOPE],
};

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

const Authenticator = () => {
    const application = useApplication();
    const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(null);
    const [currentAccessToken, setCurrentAccessToken] = useState<string | null>(null);
    const { instance, accounts, inProgress } = useMsal();
    const { state: user, dispatch: userDispatch } = useUser();
    const [error, setError] = useState(null);

    const isAuthenticated = useIsAuthenticated();

    const loadUserSiteMap = async () => {
        return getUserSiteMap();
    };

    useEffect(() => {
        const cId: string | null = instance.addEventCallback((message: EventMessage) => {
            if (message.eventType === EventType.LOGOUT_SUCCESS) {
                window.localStorage.removeItem("accessToken");
                window.localStorage.removeItem("siteMap");
                window.localStorage.removeItem("users");
                userDispatch({ type: SET_TOKEN, payload: null });
            }
        });

        return () => {
            if (cId) instance.removeEventCallback(cId);
        };
    }, [instance]);

    useEffect(() => {
        if (!isAuthenticated && inProgress === "none") {
            instance.loginRedirect(loginRequest).catch((e: any) => {
                setActiveAccount(null);
                setError(e);
            });
        }
    }, [inProgress, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && inProgress === "none" && currentAccessToken) {
            loadUserSiteMap().then((data) => {
                window.localStorage.setItem("siteMap", JSON.stringify(data));
            });
        }
    }, [inProgress, isAuthenticated, currentAccessToken]);

    const globalData: any = (window as any).heatUserData;

    const getActiveAccount = useCallback(() => {
        if (accounts.length === 0) return;
        const currentAccount = accounts[0];
        let isExpiredToken = false;

        if (globalData && globalData.nextVersion && globalData.nextVersion >= 2) {
            console.log("[HEAT NEXT] Running in non-legacy architecture.");
            instance
                .acquireTokenSilent(loginRequest)
                .then((response: any) => {
                    const { _accessToken, _jwt, _legacyToken, nextToken } = response;
                    setCurrentAccessToken(nextToken);

                    const decoded_jwt: any = globalData;

                    const mapLegacyRoleToHeatRole = (heatRole: number) => {
                        switch (heatRole) {
                            case HeatRoles.Admin:
                                return [Roles.Admin, Roles.Instructor, Roles.Trainee];
                            case HeatRoles.Instructor:
                                return [Roles.Instructor, Roles.Trainee];
                            case HeatRoles.Trainee:
                                return [Roles.Trainee];
                            default:
                                return [Roles.Trainee];
                        }
                    };

                    const heatRole = decoded_jwt.role;

                    const role = mapLegacyRoleToHeatRole(heatRole)[0];

                    userDispatch({ type: SET_ROLE, payload: role });
                    userDispatch({ type: SET_TOKEN, payload: nextToken });

                    setActiveAccount(globalData);

                    window.localStorage.setItem("accessToken", nextToken);
                })
                .catch((e: any) => {
                    setActiveAccount(null);

                    if (e instanceof InteractionRequiredAuthError)
                        return (msalInstance as StubPublicClientApplication).acquireTokenRedirect(
                            loginRequest,
                        );
                    // fallback to interaction when silent call fails
                    else setError(e);
                })
                .finally(() => application.setLoaderQueueResolved("AUTH"));

            return;
        }

        if (currentAccessToken) {
            const decodedToken: any = safeJwtDecode(currentAccessToken);
            const expirationTime =
                typeof decodedToken?.exp === "number" ? decodedToken.exp * 1000 : undefined;
            isExpiredToken = expirationTime ? expirationTime < Date.now() : true;
        }

        if (!activeAccount || isExpiredToken) {
            setActiveAccount(currentAccount);

            instance.setActiveAccount(currentAccount);
            instance
                .acquireTokenSilent(loginRequest)
                .then((response: any) => {
                    const { accessToken, jwt, legacyToken } = response;
                    setCurrentAccessToken(accessToken);

                    const token = isJwtLike(legacyToken)
                        ? legacyToken
                        : isJwtLike(jwt)
                          ? jwt
                          : null;

                    const decoded_jwt: any = safeJwtDecode(token);

                    if (!token || !decoded_jwt) {
                        window.localStorage.removeItem("accessToken");
                        setActiveAccount(null);
                        throw new InteractionRequiredAuthError(
                            "No valid JWT token found in auth response.",
                        );
                    }

                    // Legacy MSAL tokens expose role via `extension_IsHeat{Role}` booleans.
                    // HeatAuth stub tokens (and some deployments) expose a numeric `role` claim instead.
                    const roleFromNumericClaim =
                        typeof decoded_jwt?.role === "number"
                            ? decoded_jwt.role === HeatRoles.Admin
                                ? Roles.Admin
                                : decoded_jwt.role === HeatRoles.Instructor
                                  ? Roles.Instructor
                                  : decoded_jwt.role === HeatRoles.Trainee
                                    ? Roles.Trainee
                                    : undefined
                            : undefined;

                    const role =
                        roleFromNumericClaim ??
                        Object.values(Roles).filter(
                            (value) => decoded_jwt[`extension_IsHeat${value}`],
                        )[0];

                    userDispatch({ type: SET_ROLE, payload: role });
                    userDispatch({ type: SET_TOKEN, payload: token });

                    window.localStorage.setItem("accessToken", accessToken);
                })
                .catch((e: any) => {
                    setActiveAccount(null);

                    if (e instanceof InteractionRequiredAuthError)
                        // new versions of msal public don't include the call anymore...
                        // so we're casting as the stub version for the time being
                        return (msalInstance as StubPublicClientApplication).acquireTokenRedirect(
                            loginRequest,
                        );
                    // fallback to interaction when silent call fails
                    else setError(e);
                })
                .finally(() => application.setLoaderQueueResolved("AUTH"));
        }
    }, [accounts, application, instance, activeAccount, currentAccessToken]);

    if (globalData && !window.hasVerifiedUser) {
        window.setTimeout(() => {
            getActiveAccount();
            window.hasVerifiedUser = true;
        }, 500);
    }

    useEffect(() => {
        window.setTimeout(() => {
            getActiveAccount();
            const heatData = (window as any).heatUserData;

            if (heatData) {
                window.hasVerifiedUser = true;
            }
        }, 200);
    }, [window.location.href]);

    const isUserAuthenticated = activeAccount && isAuthenticated;

    if (error && !isDev) {
        return <GenericError />;
    }

    if (!user.id && user.status !== "RESOLVED") {
        return null;
    }

    return isUserAuthenticated ? (
        <FeatureTogglesProvider>
            <VRIntlProviderComponent localeFn={localeFn} id="app" fallback={null}>
                <Content />
            </VRIntlProviderComponent>
        </FeatureTogglesProvider>
    ) : null;
};

const Content = () => {
    const { state: user } = useUser();
    const location = useLocation();
    const isHeadLessRoute = HeadlessPages.find((page) => location.pathname === page);

    // In dev mode we use auth stubs and can end up without a populated `user.role`.
    // Default to Admin locally so route gating (like `/reports`) behaves as expected during development.
    const effectiveRole = isDev && !user.role ? Roles.Admin : user.role;
    const isAdminOrInstructor = effectiveRole === Roles.Instructor || effectiveRole === Roles.Admin;

    const hasOverview = () => {
        const siteMapString = window.localStorage.getItem("siteMap") as string;
        const siteMap = JSON.parse(siteMapString);
        const hasOverview = siteMap
            ?.find((nav: any) => nav.title.toLowerCase() === "dashboard")
            ?.navigationItems.some((item: any) => item.title.toLowerCase() === "overview");

        return hasOverview;
    };

    const renderPage = () => {
        if (user.acceptedDataPrivacy) {
            if (user.registrationCompleted) {
                return hasOverview() ? (
                    <Navigate to={Pages.Overview} />
                ) : (
                    <Navigate to={Pages.Sessions} />
                );
            } else {
                return <Navigate to={Pages.Welcome} />;
            }
        }

        return <Navigate to={Pages.Privacy} />;
    };

    return (
        <Wrapper className={isHeadLessRoute ? "head-less" : ""}>
            {!isHeadLessRoute && (
                <>
                    <Background />
                    <TopBar />
                    <Navigation />
                </>
            )}

            <Routes>
                <Route path={Pages.Promo} element={<PromoPage />} />

                <Route path={Pages.Overview} element={<OverviewPage />} />
                {effectiveRole === Roles.Admin && (
                    <Route path={Pages.Users} element={<UsersPage />} />
                )}
                {/* Always register `/reports` so navigation never hits an unmatched route.
                    Access is still enforced via redirect for non-admin/instructor users. */}
                <Route
                    path={Pages.Reports}
                    element={isAdminOrInstructor ? <Reports /> : <Navigate to={Pages.Sessions} />}
                />
                <Route path={Pages.Settings} element={<Settings />} />

                <Route path={Pages.Sessions} element={<SessionsPage />} />
                <Route path={Pages.SessionDetail} element={<SessionDetail />} />

                {/* on-boarding routes */}
                <Route path={Pages.Privacy} element={<PrivacyStatement />} />
                <Route path={Pages.Welcome} element={<OnboardingSurvey />} />
                <Route path={Pages.Success} element={<SurveySuccess />} />

                <Route path="/" element={renderPage()} />
            </Routes>
        </Wrapper>
    );
};

const msalInstance = new PublicClientApplication({
    ...msalConfig,
    auth: {
        clientId: process.env.REACT_APP_MSAL_CLIENT_ID,
        authority: process.env.REACT_APP_MSAL_AUTHORITY,
        redirectUri: window.location.origin,
        knownAuthorities: [process.env.REACT_APP_MSAL_KNOWN_AUTHORITIES],
    },
});

const client = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            retryDelay: 3000,
            suspense: true,
            useErrorBoundary: true,
            refetchOnWindowFocus: false,
        },
    },
});

const App = () => {
    const application = useApplication();

    return (
        <MsalProvider instance={msalInstance}>
            <ThemeProvider theme={base}>
                <GlobalStyle />
                <QueryClientProvider client={client}>
                    <VRIntlSuspender fallback={null}>
                        <BrowserRouter>
                            <RouteHistoryProvider>
                                <UserProvider id={null}>
                                    <ModalProvider>
                                        <Authenticator />
                                        <ModalWithContext />
                                    </ModalProvider>
                                </UserProvider>
                            </RouteHistoryProvider>
                        </BrowserRouter>
                    </VRIntlSuspender>
                </QueryClientProvider>
                {!application.isLoaderQueueReady() && (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                )}
            </ThemeProvider>
        </MsalProvider>
    );
};

const ApplicationWrapper = () => (
    <ApplicationProvider>
        <App />
    </ApplicationProvider>
);

export default ApplicationWrapper;
